import { FC, memo, useContext, useMemo } from 'react';
import { apiV2EnergyOverviewGetEnergyUsageOverviewPost } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';

import styles from './index.module.scss';
import { isEmpty } from 'lodash-es';
import { Carousel, Empty, Progress, Spin } from 'antd';
import React from 'react';
import { formatName, getDefaultTime } from '@/pages/WgqAllMaps/utils';
import { formatThousandAndUnit, keepOneOrLine } from '@/utils/utils';
import { ShareDataUnityEnergyCockpitMapContext } from '../..';
import { IndexTimeActive } from '../LeftBox/components/SwitchDate';
import dayjs from 'dayjs';

interface ICenterTopBox {
  className?: string;
}

const CenterTopBox: FC<ICenterTopBox> = ({ className }) => {
  const { timeResolution, dateValue } = useContext(ShareDataUnityEnergyCockpitMapContext);
  const { loading, data } = useRequest(
    () => {
      return apiV2EnergyOverviewGetEnergyUsageOverviewPost({
        fromTime: getDefaultTime(timeResolution!, dateValue)?.startTime,
        toTime: getDefaultTime(timeResolution!, dateValue)?.endTime,
        timeResolution,
      });
    },
    {
      refreshDeps: [timeResolution, dateValue],
      ready: !!timeResolution && !!dateValue,
    }
  );

  const dateLabel = useMemo(() => {
    const date = getDefaultTime(timeResolution!, dateValue).startTime;
    // 如果是按年，就显示对应年，如2026年，如果是月，就显示对应年月，如2026年1月
    return timeResolution === IndexTimeActive.THIS_YEAR
      ? dayjs(date).format('YYYY年')
      : timeResolution === IndexTimeActive.THIS_MONTH
      ? dayjs(date).format('YYYY年M月')
      : '';
  }, [timeResolution, dateValue]);

  return (
    <Spin spinning={loading}>
      <div className={`${styles.topRow} ${className ? styles[className] : ''}`}>
        <div className={`${styles.item}`}>
          <div className={styles.title}>{dateLabel}-用能总量</div>
          <div className={styles.energyInfo}>
            <div className={styles.left}>
              <div className={styles.subTitle}>
                计划标煤(kgce):
                <div className={styles.value}>
                  {formatThousandAndUnit(data?.planUsage).value}
                  <span className={styles.unit}>{formatThousandAndUnit(data?.planUsage).unit}</span>
                </div>
              </div>
              <div className={styles.subTitle}>
                实际标煤(kgce):
                <div className={styles.value}>
                  {formatThousandAndUnit(data?.totalUsage).value}
                  <span className={styles.unit}>{formatThousandAndUnit(data?.totalUsage).unit}</span>
                </div>
              </div>
            </div>
            <div className={styles.right}>
              <Progress
                type="circle"
                size={28}
                percent={data?.planProportion ? Number(data?.planProportion) : 0}
                trailColor="#4A90E233"
                showInfo={false}
                strokeColor="#5BFF60"
              />
              {keepOneOrLine(data?.planProportion ? Number(data?.planProportion) : undefined) + '%'}
            </div>
          </div>
        </div>
        <div className={`${styles.item}`}>
          <div className={styles.title}>{dateLabel}-用电量</div>
          <div className={styles.energyInfo}>
            <div className={styles.left}>
              <div className={styles.subTitle}>
                计划电能({data?.electricDetail?.physicalUnitName ?? '--'}):
                <div className={styles.value}>
                  {formatThousandAndUnit(data?.electricDetail?.planUsage).value}
                  <span className={styles.unit}>{formatThousandAndUnit(data?.electricDetail?.planUsage).unit}</span>
                </div>
              </div>
              <div className={styles.subTitle}>
                实际电能({data?.electricDetail?.physicalUnitName ?? '--'}):
                <div className={styles.value}>
                  {formatThousandAndUnit(data?.electricDetail?.usage).value}
                  <span className={styles.unit}>{formatThousandAndUnit(data?.electricDetail?.usage).unit}</span>
                </div>
              </div>
            </div>
            <div className={styles.right}>
              <Progress
                type="circle"
                size={28}
                percent={data?.electricDetail?.planProportion ? Number(data?.electricDetail?.planProportion) : 0}
                trailColor="#4A90E233"
                showInfo={false}
                strokeColor="#5BFF60"
              />
              {keepOneOrLine(
                data?.electricDetail?.planProportion ? Number(data?.electricDetail?.planProportion) : undefined
              ) + '%'}
            </div>
          </div>
        </div>
        <div className={`${styles.item} ${styles.water}`}>
          <div className={styles.title}>{dateLabel}-用水量</div>
          {isEmpty(data?.waterDetail) ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Carousel autoplay autoplaySpeed={2000} dotPosition="right" dots={false} className={styles.usageInfo}>
              {data?.waterDetail?.map((item, idx) => (
                <div key={idx} className={styles.energyInfo}>
                  <div className={styles.left}>
                    <div className={styles.subTitle}>
                      计划{formatName(item.name)}({item.physicalUnitName ?? '-'}):
                      <span className={styles.value}>
                        {formatThousandAndUnit(item.planUsage).value}
                        <span className={styles.unit}>{formatThousandAndUnit(item.planUsage).unit}</span>
                      </span>
                    </div>
                    <div className={styles.subTitle}>
                      实际{formatName(item.name)}({item.physicalUnitName ?? '-'}):
                      <span className={styles.value}>
                        {formatThousandAndUnit(item.usage).value}
                        <span className={styles.unit}>{formatThousandAndUnit(item.usage).unit}</span>
                      </span>
                    </div>
                  </div>
                  <div className={styles.right}>
                    <Progress
                      type="circle"
                      size={28}
                      percent={item?.planProportion ? Number(item?.planProportion) : 0}
                      trailColor="#4A90E233"
                      showInfo={false}
                      strokeColor="#5BFF60"
                    />
                    {keepOneOrLine(item?.planProportion ? Number(item?.planProportion) : undefined) + '%'}
                  </div>
                </div>
              ))}
            </Carousel>
          )}
        </div>
        <div className={`${styles.item} ${styles.gas}`}>
          <div className={styles.title}>{dateLabel}-用气量</div>
          {isEmpty(data?.gasDetail) ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Carousel autoplay autoplaySpeed={2000} dotPosition="right" dots={false} className={styles.usageInfo}>
              {data?.gasDetail?.map((item, idx) => (
                <div key={idx} className={styles.energyInfo}>
                  <div className={styles.left}>
                    <div className={styles.subTitle}>
                      计划{formatName(item.name)}({item.physicalUnitName ?? '-'}):
                      <span className={styles.value}>
                        {formatThousandAndUnit(item.planUsage).value}
                        <span className={styles.unit}>{formatThousandAndUnit(item.planUsage).unit}</span>
                      </span>
                    </div>
                    <div className={styles.subTitle}>
                      实际{formatName(item.name)}({item.physicalUnitName ?? '-'}):
                      <span className={styles.value}>
                        {formatThousandAndUnit(item.usage).value}
                        <span className={styles.unit}>{formatThousandAndUnit(item.usage).unit}</span>
                      </span>
                    </div>
                  </div>
                  <div className={styles.right}>
                    <Progress
                      type="circle"
                      size={28}
                      percent={item?.planProportion ? Number(item?.planProportion) : 0}
                      trailColor="#4A90E233"
                      showInfo={false}
                      strokeColor="#5BFF60"
                    />
                    {keepOneOrLine(item?.planProportion ? Number(item?.planProportion) : undefined) + '%'}
                  </div>
                </div>
              ))}
            </Carousel>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default memo(CenterTopBox);
